/* import __COLOCATED_TEMPLATE__ from './list-filter.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { REPORTING_FILTER_SELECT_ALL } from 'embercom/lib/reporting/flexible/constants';
import ConversationAttributeDescriptor from 'embercom/models/conversation-attributes/descriptor';

export default class ListFilter extends Component {
  @service appService;
  @service store;
  @service intercomEventService;
  @service intl;
  @tracked label = this.currentLabel;
  // store the selection here so that the UI doesn't update until the drop down is closed
  @tracked values = this.startingValues;
  @tracked operator = this.startingOperator;

  // Stores the latest selection from IcMultiselect without triggering a re-render.
  // Applied to this.values only when the dropdown closes, preventing the items array
  // from recomputing mid-session and resetting scroll position.
  _pendingValues = null;

  get operatorLabelMappings() {
    return {
      category: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.custom-attributes.boolean-values.is',
      ),
      not_in_category: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.custom-attributes.boolean-values.is-not',
      ),
      exists: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.custom-attributes.boolean-values.exists',
      ),
      not_exists: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.custom-attributes.boolean-values.not-exists',
      ),
    };
  }

  get startingValues() {
    let defaultValues = this.listOptions[0] ? [this.listOptions[0].id] : [];
    let values = this.args.selected.values || defaultValues;
    return values[0] === REPORTING_FILTER_SELECT_ALL ? [] : values;
  }

  get startingOperator() {
    return this.args.selected.operator || 'category';
  }

  get customField() {
    let identifier = this.args.type;
    return ConversationAttributeDescriptor.peekByIdentifier(identifier);
  }

  get listOptions() {
    let options = this.customField.listOptions;

    // Filter out archived options for system-defined or classification attributes if feature is enabled
    if (this.customField.systemDefined || this.customField.isClassificationAttribute) {
      let hideArchived = this.appService?.app?.canUseFeature('hide-archived-sda-options');
      if (hideArchived) {
        options = options.filter((option) => option && !option.archived);
      }
    }

    return options
      .map((option) => ({
        id: option.listOptionId,
        label: option.label,
        archived: option.archived,
      }))
      .sort((a, b) => a.archived - b.archived);
  }

  get items() {
    return [
      {
        items: this.allItems,
      },
    ];
  }

  get allItems() {
    return this.listOptions.map((option) => ({
      text: option.label,
      value: option.id,
      isSelected: this.values.includes(option.id),
      count: option.archived
        ? this.intl.t(
            'components.reporting.custom.chart-builder.filter-bar.custom-attributes.boolean-values.archived',
          )
        : '',
    }));
  }

  get currentLabel() {
    switch (this.operator) {
      case 'category':
      case 'not_in_category': {
        let label =
          this.optionNames ||
          this.intl.t(
            'components.reporting.custom.chart-builder.filter-bar.custom-attributes.boolean-values.any',
          );
        return `${this.operatorLabelMappings[this.operator]} ${label}`;
      }
      case 'exists':
      case 'not_exists':
        return this.operatorLabelMappings[this.operator];
    }
  }

  get optionNames() {
    return this.values
      .map((optionId) => {
        return this.listOptions.find((option) => option.id === optionId)?.label;
      })
      .compact()
      .join(', ');
  }

  get openerLabel() {
    return (
      this.optionNames ||
      this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.custom-attributes.boolean-values.add',
      )
    );
  }

  get valuesForFilter() {
    let valuesOrPlaceholder = this.values.length ? this.values : [REPORTING_FILTER_SELECT_ALL];
    return ['category', 'not_in_category'].includes(this.operator) ? valuesOrPlaceholder : [];
  }

  @action
  onSelectionChange(values) {
    this._pendingValues = values;
  }

  @action
  onClose() {
    if (this._pendingValues !== null) {
      this.values = this._pendingValues;
      this._pendingValues = null;
    }

    if (!this.valuesForFilter.length && !this.operator) {
      return;
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: 'filtered_custom_field',
      object: this.args.analyticsObject,
      custom_field_type: 'list',
    });

    this.args.setSelected(this.valuesForFilter, this.operator);
    this.label = this.currentLabel;
  }

  @action
  removeFilter() {
    this.values = [];
    this.operator = null;
    this.args.removeFilter();
  }
}
